export type LocalDraftKind = "ai" | "simple" | "upload" | "scan";

export type LocalDraft = {
  id: string;
  title: string;
  kind: LocalDraftKind;
  text: string;
  html?: string;
  createdAt: string;
  updatedAt: string;
  /** Marked unsaved — may be lost until login + cloud save */
  unsaved: true;
  attachments?: Array<{ name: string; content: string; mime?: string }>;
};

const KEY = "nevisign.reader.local-drafts.v1";

function readAll(): LocalDraft[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LocalDraft[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items: LocalDraft[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(0, 40)));
  } catch {
    /* ignore */
  }
}

export function listLocalDrafts(): LocalDraft[] {
  return readAll().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getLocalDraft(id: string): LocalDraft | null {
  return readAll().find((d) => d.id === id) ?? null;
}

export function upsertLocalDraft(
  draft: Omit<LocalDraft, "createdAt" | "updatedAt" | "unsaved"> & {
    createdAt?: string;
    updatedAt?: string;
  },
): LocalDraft {
  const now = new Date().toISOString();
  const items = readAll().filter((d) => d.id !== draft.id);
  const prev = readAll().find((d) => d.id === draft.id);
  const next: LocalDraft = {
    id: draft.id,
    title: draft.title || "پیش‌نویس بدون عنوان",
    kind: draft.kind,
    text: draft.text,
    html: draft.html,
    attachments: draft.attachments,
    createdAt: draft.createdAt ?? prev?.createdAt ?? now,
    updatedAt: draft.updatedAt ?? now,
    unsaved: true,
  };
  items.unshift(next);
  writeAll(items);
  return next;
}

export function removeLocalDraft(id: string): void {
  writeAll(readAll().filter((d) => d.id !== id));
}

export function newDraftId(): string {
  return `draft_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
